<template>
  <div>
    <b-input-group class="date-filter-group">
      <b-form-input
        id="datepicker-input"
        v-model="date"
        @blur="handleBlur"
        type="text"
        :placeholder="properties.placeholder"
        autocomplete="off"
        style="line-height: 1rem; width: 100%;"
        :ref="`${properties.filterName}_date_filter`"
      ></b-form-input>
      <el-date-picker
        class="date-filter-calendar"
        :model-value="pickerValue"
        type="date"
        value-format="YYYY-MM-DD"
        :clearable="false"
        :editable="false"
        @update:model-value="onPick"
      />
    </b-input-group>
  </div>
</template>
<script>
  import moment from 'moment'
  export default {
    name: 'dateFilter',
    props: {
      properties: Object
    },
    data () {
      return {
        date: '',
        locale: window.navigator.userLanguage || window.navigator.language || 'en-US' // 'fr-fr' // test stuff,
      }
    },
    computed: {
      pickerValue () {
        return /^\d{4}-\d{2}-\d{2}$/.test(this.date) ? this.date : ''
      }
    },
    methods: {
      /**
       * Calendar selection. Writes the picked YYYY-MM-DD into the same `date` model the text input
       * uses and runs the identical sync/unset path as handleBlur, so filter events are unchanged.
       */
      onPick (val) {
        this.date = val || ''
        this.properties.sync(this.properties, this.date)
        if (!this.date) {
          this.$store.dispatch('unsetFilter', this.properties.filterName + `__${this.properties.suffix}`)
        }
      },
      /**
       * Handles blur event. Emits a blur event (standard), as well as an input event for v-model update after date is
       * formatted to the correct format. Also fires a change event since the value is changed after it is formatted.
       * @param e   FocusEvent
       */
      handleBlur (e) {
        if (this.date) {
          var m = moment(this.date, 'YYYY-MM-DD')
          if (m.isValid()) {
            let parseddate = this.parseUserDateFormats(this.date)
            this.date = parseddate.year + '-' + parseddate.month + '-' + parseddate.day
            this.properties.sync(this.properties, this.date)
          } else {
            this.errHandler(e.message)
            return
          }
        } else {
          this.properties.sync(this.properties, this.date)
          this.$store.dispatch('unsetFilter', this.properties.filterName + `__${this.properties.suffix}`)
        }
      },
      /**
       * Displays an error notification to the user
       * @param msg   The error message
       */
      errHandler (msg) {
        return this.$message({
          showClose: false,
          message: msg || 'Invalid date format. Format should be YYYY-MM-DD',
          type: 'error'
        })
      },
      /**
       * Parses user provided date string into Object containing day, month, and year
       * @return {object}
       */
      parseUserDateFormats (dateString) {
        let date = dateString.split('/').join(',').split('-').join(',').split(',')
        if (!date) return ''
        let ret = { day: '', month: '', year: '' }
        switch (date.length) {
          case 3:
            const chkDM = (digits) => (digits && digits.length <= 2)
            const chkYr = (digits, len) => (digits && digits.length === len)
            if (chkDM(date[0]) && chkDM(date[1]) && chkYr(date[2], 4)) {          // MM/DD/YYYY
              ret.year = date[2]
              ret.day = date[1]
              ret.month = date[0]
            } else if (chkDM(date[0]) && chkDM(date[1]) && chkYr(date[2], 2)) {   // MM/DD/YY || M/D/YY
              ret.year = this.parseTwoDigitYear(date[2])
              ret.day = date[1]
              ret.month = date[0]
            } else if (chkYr(date[0], 4) && chkDM(date[1]) && chkDM(date[2])) {   // YYYY/MM/DD
              ret.year = date[0]
              ret.day = date[2]
              ret.month = date[1]
            } else {
              return null
            }
            break
          case 2:
            const defaultToFirstDay = (monthLength) => {
              if (monthLength === 1) {
                return '1'
              } else if (monthLength === 2) {
                return '01'
              }
            }
            if (date[0].length === 4) {         // YYYY/MM
              ret.year = date[0]
              ret.month = date[1]
              ret.day = defaultToFirstDay(ret.month.length)
            } else if (date[0].length <= 2) {   // MM/DD
              ret.month = date[0]
              ret.day = date[1]
            }
            break
          case 1:
            ret = this.handleUndelimitedDateString(date[0])   // YYYYMMDD || MMDDYYYY or inverse
            break
        }
        return ret
      },
      /**
       * Parses user-provided date string when string contains no delimiters (i.e. '/' or '-')
       * Arranges the date string in two possible ways, the first being one where the year
       * might be at the start (yearIsAtStart) and the second being one where the year might
       * be at the end (yearIsAtEnd), then compare yearIsAtStart and yearIsAtEnd to determine
       * which is more likely to be a valid date. If nothing matches, returns null.
       * @param date
       * @return {*}
       */
      handleUndelimitedDateString (date) {
        if (!(date.length === 8 || date.length === 6)) return null
        let yearIsAtStart, yearIsAtEnd, firstTwoDigits, lastTwoDigits
        if (date.length === 8) {
          yearIsAtStart = [   // YYYYMMDD
            date.substring(0, 4),
            date.substring(4, 6),
            date.substring(6, 8)
          ]
          yearIsAtEnd = [     // MMDDYYYY
            date.substring(0, 2),
            date.substring(2, 4),
            date.substring(4, 8)
          ]
          firstTwoDigits = parseInt(yearIsAtStart[0][0] + yearIsAtStart[0][1])
          if (firstTwoDigits > 12 && [19, 20, 21].includes(firstTwoDigits)) {
            return { day: yearIsAtStart[2], month: yearIsAtStart[1], year: yearIsAtStart[0] }
          }
          if (firstTwoDigits <= 12) {
            return { day: yearIsAtEnd[1], month: yearIsAtEnd[0], year: yearIsAtEnd[2] }
          }
        } else if (date.length === 6) {
          yearIsAtEnd = [     // MMDDYY
            date.substring(0, 2),
            date.substring(2, 4),
            date.substring(4, 6)
          ]
          firstTwoDigits = parseInt(yearIsAtEnd[0][0] + yearIsAtEnd[0][1])
          lastTwoDigits = parseInt(yearIsAtEnd[2][0] + yearIsAtEnd[2][1])
          const getCentury = (twoDigitYear) => {
            return twoDigitYear >= 50 && twoDigitYear <= 99 ? 1900 : 2000
          }
          if (firstTwoDigits <= 12) {
            return { day: yearIsAtEnd[1], month: yearIsAtEnd[0], year: getCentury(lastTwoDigits) + lastTwoDigits }
          }
        }
        return null
      },
      /**
       * Parses a two digit year, returning 20xx if year < 50, or 19xx if year > 50
       * @param year
       */
      parseTwoDigitYear (year) {
        return year >= 50 ? parseInt(`19${year}`) : parseInt(`20${year}`)
      },
      onContext (ctx) {
        // The date formatted in the locale, or the `label-no-date-selected` string
        // The following will be an empty string until a valid date is entered
        if (ctx.selectedDate) {
          this.properties.sync(this.properties, ctx.selectedYMD)
        }
      }
    }
  }
</script>
<style scoped>
  .date-field {
    background-color: #fff;
    background-image: none;
    border-radius: 4px;
    border: 1px solid #BFCBD9;
    box-sizing: border-box;
    color: #1F2D3D;
    font-size: inherit;
    height: 36px;
    line-height: 1;
    outline: 0;
    padding: 3px 10px;
    transition: border-color .2s cubic-bezier(.645,.045,.355,1);
    width: 100%;
  }
  .b-form-datepicker .dropdown-toggle{
    padding: 4px !important;
    font-size: 11px !important;
  }
  /* main.js loads bootstrap-vue-next.css but NOT bootstrap core, so `.input-group` has no
     `display:flex` base rule — the text input and the calendar would stack as two rows. Re-establish
     the horizontal input-group layout here so the field + calendar sit inline as one control. */
  .date-filter-group {
    display: flex;
    flex-wrap: nowrap;
    align-items: stretch;
    width: 100%;
  }
  .date-filter-group :deep(.form-control) {
    flex: 1 1 auto;
    width: 1%;
    min-width: 0;
  }
  /* Layout only: keep the calendar trigger from growing so the text field takes the remaining
     width. The el-date-picker internals (border/background/width) are overridden in the plain
     <style> block below — element-plus nodes can't be reached from scoped styles without :deep(). */
  .date-filter-calendar {
    width: auto;
    flex: 0 0 auto;
  }
</style>
<style>
  .b-form-datepicker .btn-sm {
    padding: 0 0.2rem !important;
    border: none !important;
  }
  #datepicker-input {
    font-size: 12px;
    border: none !important;
    width: 300px !important;
    padding-left: 6px;
  }
  .date-filter-calendar > .el-input__wrapper > .el-input__prefix {
    font-size: 15px !important;
    font-weight: 900 !important;
    color: #3c3636 !important;
  }
  .date-filter-calendar > .el-input__wrapper {
    /* width: auto !important; */
    /* padding: 0 4px; */
    /* background-color: transparent; */
    box-shadow: none !important;
    width: 0px !important;
    border: none !important;
    background: transparent !important;
    /* color: #323030; */
    cursor: pointer;
    box-shadow: none;
  }
  .form-control {
    display: block;
    width: 100%;
    /* height: calc(1.5em + .75rem + 2px); */
    padding: .375rem .75rem;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: #495057;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid #ced4da;
    border-radius: .25rem;
    transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;
}
</style>
